import React, { useEffect } from 'react';
import { MemoryRouter, useInRouterContext } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/primitives';
import { SegmentedControl } from '../../components/primitives';
import { AppShell } from '../../components/shell';
import { Logo } from '../../components/Logo/Logo';
import { SectionCard } from '../../components/SectionCard';
import { Timeline } from '../../components/Timeline';
import { Stepper } from '../../components/Stepper';
import { RadioRows } from '../../components/RadioRows';
import { EmptyState } from '../../components/EmptyState';
import { applySemanticThemeTokens, SemanticThemeInput } from '../../helpers/theme';
import { tailwindcssPaletteGenerator } from '../../helpers/tailwind';
import { THEME_PREVIEW_MESSAGE_SOURCE } from 'config-types';

export { THEME_PREVIEW_MESSAGE_SOURCE };

type SetThemeMessage = {
  source: typeof THEME_PREVIEW_MESSAGE_SOURCE;
  type: 'set-theme';
  theme: SemanticThemeInput;
};

const isSetThemeMessage = (data: unknown): data is SetThemeMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return (
    msg.source === THEME_PREVIEW_MESSAGE_SOURCE &&
    msg.type === 'set-theme' &&
    typeof msg.theme === 'object' &&
    msg.theme !== null
  );
};

/**
 * Applies an in-flight (unsaved) theme draft to the whole document — both the
 * semantic tokens and the legacy --app-* shade ramp still used by a few
 * numeric-shade utilities, mirroring what ConfigProvider does with the saved
 * theme at boot.
 */
export const applyDraftTheme = (theme: SemanticThemeInput): void => {
  applySemanticThemeTokens(theme);
  if (theme.primaryColor) tailwindcssPaletteGenerator('primary', theme.primaryColor);
  if (theme.secondaryColor) tailwindcssPaletteGenerator('secondary', theme.secondaryColor);
};

const sampleOrders = [
  { id: '#1042', product: 'Aurora Table Lamp', status: 'Fulfilled', total: '$129.00' },
  { id: '#1041', product: 'Oak Side Table', status: 'Pending', total: '$349.00' },
  { id: '#1040', product: 'Linen Cushion Set', status: 'Fulfilled', total: '$39.00' },
];

/**
 * Unauthenticated static showcase of the partner app's design system, iframed
 * by the admin theming editor as its live preview. The admin posts unsaved
 * theme drafts into this page; nothing here talks to an API. Applying a draft
 * only mutates CSS variables inside this document, so messages are validated
 * by shape rather than origin — a hostile embedder could only recolor its own
 * iframe.
 */
export const ThemePreview: React.FC = () => {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isSetThemeMessage(event.data)) return;
      applyDraftTheme(event.data.theme);
    };
    window.addEventListener('message', onMessage);
    // Content-free handshake so the parent knows the listener is mounted and
    // can (re)send the current draft. '*' is fine here: the ping carries no data.
    window.parent?.postMessage({ source: THEME_PREVIEW_MESSAGE_SOURCE, type: 'ready' }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // The REAL app shell (sidebar nav, top bar, user chip) so the preview is an
  // honest miniature of the partner admin, not an approximation. The shell's
  // NavLinks need a router; in the app this page renders outside the router
  // (it bypasses auth-state routing) so a MemoryRouter hosts it — but in
  // Storybook the preview decorator already provides one, and react-router
  // throws on nested routers, hence the context check.
  const inRouter = useInRouterContext();
  const Host: React.FC<{ children: React.ReactNode }> = inRouter
    ? ({ children }) => <>{children}</>
    : ({ children }) => <MemoryRouter initialEntries={['/']}>{children}</MemoryRouter>;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="theme-preview-root">
      <Host>
        <AppShell
          brand={<Logo maxWidth={120} maxHeight={28} />}
          user={{ name: 'Preview Seller', email: 'seller@example.com', initials: 'PS' }}
        >
          <div className="mx-auto max-w-6xl space-y-6 px-8 py-8">
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Theme preview — changes in the editor show up here instantly; nothing is
                  saved until you press Save.
                </p>
              </div>
              <Button size="sm">Add product</Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: 'Total sales', value: '$12,480' },
                { label: 'Orders', value: '86' },
                { label: 'Pending payout', value: '$1,905' },
              ].map((stat) => (
                <Card key={stat.label}>
                  <CardContent className="pt-6">
                    <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                    <p className="mt-1 text-3xl font-semibold tracking-tight">{stat.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent orders</CardTitle>
                <CardDescription>The latest orders across your storefront.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sampleOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">{order.id}</TableCell>
                        <TableCell>{order.product}</TableCell>
                        <TableCell>
                          <Badge variant={order.status === 'Fulfilled' ? 'success' : 'warning'}>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{order.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* The components the redesign introduced, so the theming editor
                previews the system the app actually uses rather than a subset
                of it. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <SectionCard title="Order progress" trailing="A named section">
                <Timeline
                  entries={[
                    { key: 'placed', label: 'Created', detail: 'Jul 22, 12:53 AM', state: 'done' },
                    { key: 'accepted', label: 'Accepted', detail: 'Jul 22, 9:10 AM', state: 'done' },
                    { key: 'fulfilled', label: 'Fulfilled', detail: 'Waiting on you', state: 'current' },
                    { key: 'payout', label: 'Paid', detail: 'Pending', state: 'upcoming' },
                  ]}
                />
              </SectionCard>
              <SectionCard title="Onboarding steps">
                <Stepper
                  currentKey="details"
                  steps={[
                    { key: 'invitation', label: 'Accept invitation', hint: 'Password set' },
                    { key: 'details', label: 'Your details', hint: "Who's selling" },
                    { key: 'terms', label: 'Terms', hint: 'One agreement' },
                  ]}
                />
              </SectionCard>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <SectionCard title="Choices">
                <div className="space-y-4">
                  <SegmentedControl
                    aria-label="Product type"
                    value="physical"
                    options={[
                      { value: 'physical', label: 'Physical' },
                      { value: 'digital', label: 'Digital' },
                    ]}
                  />
                  <RadioRows
                    name="preview-shipping"
                    aria-label="Shipping"
                    value="manual"
                    options={[
                      { value: 'profile', label: 'Delivery profile' },
                      {
                        value: 'manual',
                        label: 'Fixed rate',
                        summary: '$6.00 domestic',
                        content: (
                          <p className="text-sm text-muted-foreground">
                            The selected option&apos;s configuration appears here.
                          </p>
                        ),
                      },
                    ]}
                  />
                </div>
              </SectionCard>
              <SectionCard title="Nothing here yet" flush>
                <EmptyState
                  size="sm"
                  title="No products yet"
                  description="An empty state says what will fill it, and offers the one thing to do."
                />
              </SectionCard>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Buttons &amp; badges</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button>Primary</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="destructive">Delete</Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="primary">Primary</Badge>
                    <Badge>Neutral</Badge>
                    <Badge variant="success">Success</Badge>
                    <Badge variant="destructive">Destructive</Badge>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Form controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="preview-product-title">Product title</Label>
                    <Input id="preview-product-title" placeholder="Aurora Table Lamp" />
                  </div>
                  <Button size="sm">Save product</Button>
                </CardContent>
              </Card>
            </div>

            {/* Both shapes a message comes in, because the theming editor is
                previewing the real system and a shop choosing colours needs to
                see what its warning tint does to a one-line notice as well as
                to a titled panel. Only the titled one was here. */}
            <Alert variant="info" title="Theme preview">
              This is a live miniature of your sellers&apos; partner admin using the same
              application shell and components.
            </Alert>
            <Alert variant="warning">Two products are missing shipping profiles.</Alert>
          </div>
        </AppShell>
      </Host>
    </div>
  );
};
